import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, ShieldAlert, ChevronDown, ChevronRight, RefreshCw, Loader2, ExternalLink } from "lucide-react";

// READ-ONLY site-integrity report — which locations have no Site (would trip wrong-site
// transfers). Run before standing up a second yard. Report only; fixes are a separate decision.
export function SiteIntegrityCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/site-integrity").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div data-testid="site-integrity-card" className="border border-slate-200 rounded-lg p-3 mb-5 flex items-center gap-2 text-sm text-slate-400">
      <Loader2 className="w-4 h-4 animate-spin" /> Checking site integrity…
    </div>
  );
  if (!data) return null;

  const op = data.operating_locations;
  const con = data.containers;
  const clean = op.active === 0;

  return (
    <div data-testid="site-integrity-card"
      className={`border rounded-lg mb-5 ${clean ? "border-emerald-200 bg-emerald-50/50" : "border-amber-300 bg-amber-50"}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        {clean ? <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" /> : <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm text-slate-800">Site integrity</div>
          <div data-testid="site-integrity-verdict" className={`text-sm ${clean ? "text-emerald-700" : "text-amber-800 font-semibold"}`}>{data.verdict}</div>
        </div>
        <button data-testid="site-integrity-refresh" onClick={load} className="text-slate-400 hover:text-slate-700" title="Re-check"><RefreshCw className="w-4 h-4" /></button>
        <button data-testid="site-integrity-toggle" onClick={() => setOpen(!open)} className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-xs font-semibold">
          Details {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-white border border-slate-200 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Operating locations (bins / shelves / zones)</div>
            <div data-testid="site-integrity-op-count" className={`mt-1 text-2xl font-bold tabular-nums ${op.active ? "text-amber-700" : "text-emerald-700"}`}>{op.active}</div>
            <div className="text-xs text-slate-500">active without a Site · {op.count} total incl. archived</div>
            {op.items.filter((i) => !i.archived).length > 0 && (
              <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                {op.items.filter((i) => !i.archived).map((i) => (
                  <li key={i.id} className="font-mono text-xs text-amber-800">{i.code} · {i.type} · {i.name}</li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-slate-400 mt-2">This is the one that matters — a bin with no Site breaks transfer from/to.</p>
          </div>
          <div className="rounded-lg bg-white border border-slate-200 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Floating containers (totes / crates)</div>
            <div data-testid="site-integrity-con-count" className="mt-1 text-2xl font-bold tabular-nums text-slate-700">{con.active}</div>
            <div className="text-xs text-slate-500">active without a Site · {con.total} total</div>
            <div className="mt-2 text-xs">
              <span data-testid="site-integrity-con-holding" className={`font-bold ${con.live_parts_inside ? "text-amber-700" : "text-emerald-700"}`}>{con.live_parts_inside} live part(s)</span>
              <span className="text-slate-500"> inside {con.holding_live_parts} of them</span>
            </div>
            {(con.holding || []).length > 0 && (
              <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {con.holding.map((h) => (
                  <li key={h.id}>
                    <a data-testid={`site-integrity-drillin-${h.code}`} href={`/locations?view=all&q=${encodeURIComponent(h.code)}`}
                      className="inline-flex items-center gap-1 font-mono text-xs text-sky-700 hover:text-sky-900 hover:underline">
                      <ExternalLink className="w-3 h-3" />{h.code}{h.name ? ` · ${h.name}` : ""} <span className="text-slate-400">({h.part_count})</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] text-slate-400 mt-2">Usually harmless legacy lot crates — click one to open it and move the parts under a Site.</p>
          </div>
        </div>
      )}
    </div>
  );
}
